import React, { useState } from "react";

import { Card, Collapse, FormLabel, GridLegacy, Grow, Alert } from "@mui/material";

import CardBody from "../../../components/Card/CardBody";
import Button from "../../../components/CustomButtons/Button";
import TranslationWrapper from "../../../components/TranslationWrapper/TranslationWrapper.tsx";
import DistanceCostScheme from "../../../components/DistanceCostScheme/DistanceCostScheme";

import PostcodesModal from "../Modals/PostcodesModal";
import FormSwitchTC from "../../../components/Forms/FormSwitchTC";
import FormTextTC from "../../../components/Forms/FormTextTC";
import FormControlledInputTC from "../../../components/Forms/FormControlledInputTC";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useCurrencyContext } from "../../../components/Forms/CurrencyProvider";
import AuthenticationWrapper from "../../../auth/AuthenticationWrapper";
import AsyncProductAutocompleteTC from "../../../components/Forms/AsyncAutocomplete/Product/AsyncProductAutocompleteTC";
import { convertObjectToSelectOptions } from "../../../templateHelpers";
import FormSelectTC from "../../../components/Forms/FormSelectTC";
import { useSelector } from "react-redux";
import { RootState } from "../../../store.tsx";

interface DeliveryCosts {
  startDistance: number;
  stopDistance: number;
  price: number;
  id: string;
}

type DeliveryCostsScheme = DeliveryCosts[];

enum DeliveryCostsSchemeValidationError {
  // []
  NOT_FILLED_IN = "Not filled in",

  // [{from: 0, to: 1}, {from: 2, to: 3}] has gap from 1-2, undesired behavior
  // [{from: 0, to: 1}, {from: 0, to: 5}] overlaps, undefined behavior
  HAS_GAPS_OR_OVERLAPS = "Has gaps or overlaps",

  NO_DISTANCE = "No distance",
  // [{from: 0, to: 0}]
}

enum DeliveryTypes {
  REGULAR_DELIVERY = "regular_delivery",
  ALTERNATIVE_DELIVERY = "alternative_delivery",
  MIXED_DELIVERY = "mixed_delivery",
}

function validateDeliveryCostsScheme(
  delivery_costs_scheme: DeliveryCostsScheme | null
): DeliveryCostsSchemeValidationError[] {
  const errors = [];
  if (delivery_costs_scheme === null) return [DeliveryCostsSchemeValidationError.NOT_FILLED_IN];
  for (let i = 0; i < delivery_costs_scheme.length; i++) {
    const current = delivery_costs_scheme[i];

    if (current.startDistance === current.stopDistance) {
      errors.push(DeliveryCostsSchemeValidationError.NO_DISTANCE);
    }

    if (i + 1 > delivery_costs_scheme.length - 1) break;
    const next = delivery_costs_scheme[i + 1];

    if (next.startDistance !== current.stopDistance) {
      errors.push(DeliveryCostsSchemeValidationError.HAS_GAPS_OR_OVERLAPS);
    }
  }

  return Array.from(new Set(errors));
}

const deliveryOptions = {
  0: {
    value: DeliveryTypes.REGULAR_DELIVERY,
    label: "Regular delivery (Default)",
    transKey: "vestigingen.delivery.delivery_options.normal",
  },
  1: {
    value: DeliveryTypes.ALTERNATIVE_DELIVERY,
    label: "Alternative delivery",
    transKey: "vestigingen.delivery.delivery_options.alternative",
  },
  2: {
    value: DeliveryTypes.MIXED_DELIVERY,
    label: "Regular or alternative delivery",
    transKey: "vestigingen.delivery.delivery_options.both",
  },
};

function DeliverySettings() {
  const { currency } = useCurrencyContext();
  const [postcodesNLModal, setPostcodesNLModal] = useState(false);
  const { vestigingId, menukaartVestigingId } = useFormContext();

  const deliverySelectOptions = convertObjectToSelectOptions(deliveryOptions);

  const {
    field: {
      value: delivery_settings__deliveryCostSchemeValue,
      onChange: delivery_settings__deliveryCostSchemeOnChange,
    },
  } = useController({ name: "delivery_settings__deliveryCostScheme" });

  const delivery_enabled = useWatch({ name: "delivery_settings__delivery" }) === 1;
  const delivery_costs_scheme = useWatch({ name: "delivery_settings__deliveryCostScheme" });
  const delivery_costs_enabled = useWatch({ name: "delivery_settings__useDeliveryCosts" });
  const delivery_costs_scheme_errors = validateDeliveryCostsScheme(
    delivery_costs_scheme == null ? null : JSON.parse(delivery_costs_scheme)
  );
  const deliveryType = useWatch({ name: "custom_data" })?.alternative_delivery?.type || DeliveryTypes.REGULAR_DELIVERY;

  const country = useSelector((state: RootState) => state.app?.vestiging?.country);

  return (
    <>
      <Card>
        <CardBody alignItems={"flex-start"} style={{ padding: 16, display: "flex", flexWrap: "wrap" }}>
          <h3>
            <TranslationWrapper tDefault={"Algemeen"} tKey={"vestigingen.form.deliverysettings.headers.algemeen"} />
          </h3>
          <FormSwitchTC
            name={"delivery_settings__takeAway"}
            tDefault={"Ophalen"}
            tKey={"vestigingen.form.delivery_settings.label.delivery_settings__takeAway"}
          />
          <FormSwitchTC
            name={"delivery_settings__delivery"}
            tDefault={"Bezorgen"}
            tKey={"vestigingen.form.delivery_settings.label.delivery_settings__delivery"}
          />
          <Grow in={delivery_enabled} mountOnEnter unmountOnExit>
            <div style={{ marginLeft: "16px" }}>
              <FormSwitchTC
                name={"delivery_settings__useDeliveryCosts"}
                tDefault={"Charge delivery costs"}
                tKey={"DeliverySettings.chargeDeliveryCosts"}
              />
            </div>
          </Grow>
          <FormTextTC name={"rcode"} tDefault={"QR Code"} tKey={"vestigingen.form.overige.label.rcode"} />
          <FormTextTC
            name={"delivery_settings__webCodeAfhalen"}
            tDefault={"QR code afhalen"}
            tKey={"vestigingen.form.delivery_settings.label.delivery_settings__webCodeAfhalen"}
          />
          <FormTextTC
            name={"delivery_settings__webCodeBezorgen"}
            tDefault={"Web code bezorgen"}
            tKey={"vestigingen.form.delivery_settings.label.delivery_settings__webCodeBezorgen"}
          />
        </CardBody>
      </Card>
      <Grow in={delivery_enabled} mountOnEnter unmountOnExit>
        <Card style={{ marginTop: "16px" }}>
          <CardBody>
            <h3>
              <TranslationWrapper
                tDefault={"Bezorggebied"}
                tKey={"vestigingen.form.deliverysettings.headers.bezorggebied"}
              />
            </h3>

            <FormControlledInputTC
              name={"groupId"}
              rules={{
                min: {
                  value: 0,
                  message: <TranslationWrapper tKey={"The value must not be negative!"} />,
                },
              }}
              tDefault={"Link group"}
              tKey={"vestigingen.form.overige.label.groupId"}
              type={"number"}
            />

            <FormSelectTC
              defaultValue={DeliveryTypes.REGULAR_DELIVERY}
              name={"custom_data.alternative_delivery.type"}
              options={deliverySelectOptions}
              tDefault={"Delivery type"}
              tKey={"custom_data.alternative_delivery.type"}
            />
            <Collapse in={[DeliveryTypes.ALTERNATIVE_DELIVERY, DeliveryTypes.MIXED_DELIVERY].includes(deliveryType)}>
              <div style={{ marginLeft: "16px" }}>
                <Alert severity={"info"}>
                  <TranslationWrapper
                    tDefault={"Alternative delivery can be used with Mastercode 2500."}
                    tKey={"custom_data.alternative_delivery.info"}
                  />
                </Alert>
                <FormControlledInputTC
                  name={"custom_data.alternative_delivery.url"}
                  tDefault={"Alternative delivery service URL"}
                  tKey={"custom_data.alternative_delivery.url"}
                  type={"string"}
                />
              </div>
            </Collapse>
            <Collapse in={[DeliveryTypes.REGULAR_DELIVERY, DeliveryTypes.MIXED_DELIVERY].includes(deliveryType)}>
              <FormControlledInputTC
                name={"locationDistanceCheck"}
                rules={{
                  min: {
                    value: 0,
                    message: <TranslationWrapper tKey={"The value must not be negative!"} />,
                  },
                  required: {
                    value: true,
                    message: <TranslationWrapper tKey={"Set your delivery radius"} />,
                  },
                }}
                suffix={" km"}
                tDefault={"Locatie controle (straal)"}
                tKey={"vestigingen.form.overige.label.locationDistanceCheck"}
                type={"number"}
              />
              {country === "NL" ? (
                <div>
                  <FormLabel>
                    <TranslationWrapper
                      tDefault={"Postcodes NL (Black-/Whitelist)"}
                      tKey={"vestigingen.form.overige.postcodesNL.label"}
                    />
                  </FormLabel>
                  <Button color={"warning"} onClick={() => setPostcodesNLModal(true)}>
                    <TranslationWrapper tDefault={"Instellen"} tKey={"vestigingen.form.overige.postcodesNL.edit"} />
                  </Button>
                </div>
              ) : null}
            </Collapse>
          </CardBody>
        </Card>
      </Grow>
      <Grow in={delivery_costs_enabled ? delivery_enabled : null} mountOnEnter>
        <Card style={{ marginTop: "16px" }}>
          <CardBody>
            <h3>
              <TranslationWrapper tDefault={"Kosten"} tKey={"vestigingen.form.deliverysettings.headers.kosten"} />
            </h3>
            <FormControlledInputTC
              name={"delivery_settings__freeDeliveryCostsAfter"}
              rules={{
                min: {
                  value: 0,
                  message: <TranslationWrapper tKey={"The value must not be negative!"} />,
                },
              }}
              tDefault={"Gratis bezorgd boven"}
              tKey={"vestigingen.form.delivery_settings.label.delivery_settings__freeDeliveryCostsAfter"}
              type={"currency"}
            />
            <AsyncProductAutocompleteTC
              async_url={`core/producten/search?fields=id as value,naam as label,id,naam,snaam,blocked,is_actief,prijs&per_page=50&sort_field=naam&vestiging_id=${menukaartVestigingId}`}
              key={"delivery_settings__bezorgKostenArtID" + menukaartVestigingId}
              name={"delivery_settings__bezorgKostenArtID"}
              rules={{
                required: {
                  value: true,
                  message: (
                    <TranslationWrapper
                      tDefault={"Configure the product that will be used to contain your delivery costs"}
                      tKey={"Configure the product that will be used to contain your delivery costs"}
                    />
                  ),
                },
              }}
              tDefault={"Bezorg product"}
              tKey={"vestigingen.form.delivery_settings.label.delivery_settings__bezorgKostenArtID"}
            />

            <AuthenticationWrapper
              child={(write) =>
                write ? (
                  <GridLegacy item xs={12}>
                    {delivery_costs_scheme_errors.map((error) => {
                      return (
                        <div style={{ marginTop: "16px" }}>
                          <Alert severity={"error"}>
                            {error === DeliveryCostsSchemeValidationError.NO_DISTANCE ? (
                              <TranslationWrapper
                                tDefault={"Your end distance must always be higher than your start distance"}
                                tKey={"DeliveryCosts.error.no_distance"}
                              />
                            ) : error === DeliveryCostsSchemeValidationError.NOT_FILLED_IN ? (
                              <TranslationWrapper
                                tDefault={"You must configure a pricing per distance scheme"}
                                tKey={"DeliveryCosts.error.not_filled_in"}
                              />
                            ) : error === DeliveryCostsSchemeValidationError.HAS_GAPS_OR_OVERLAPS ? (
                              <TranslationWrapper
                                tDefault={"You have gaps or overlaps in your start and stop distances"}
                                tKey={"DeliveryCosts.error.HAS_GAPS_OR_OVERLAPS"}
                              />
                            ) : (
                              ""
                            )}
                          </Alert>
                        </div>
                      );
                    })}
                    <DistanceCostScheme
                      data={
                        delivery_settings__deliveryCostSchemeValue
                          ? JSON.parse(delivery_settings__deliveryCostSchemeValue)
                          : delivery_settings__deliveryCostSchemeValue
                      }
                      inputName={"delivery_settings__deliveryCostScheme"}
                      onChange={(data) => {
                        delivery_settings__deliveryCostSchemeOnChange(data);
                      }}
                      valuta={currency}
                    />
                  </GridLegacy>
                ) : null
              }
              permissionKey={"delivery_settings__deliveryCostScheme"}
            />
          </CardBody>
        </Card>
      </Grow>
      {postcodesNLModal ? (
        <PostcodesModal
          open={postcodesNLModal}
          toggle={() => setPostcodesNLModal(!postcodesNLModal)}
          vestiging_id={vestigingId}
        />
      ) : null}
    </>
  );
}

export default DeliverySettings;
